<template>
  <!-- 終了画面: 送信後「問診票アプリを終了」を選択した場合 -->
  <div v-if="terminated" class="min-h-screen flex items-center justify-center bg-white">
    <div class="text-center px-6">
      <div class="text-5xl mb-6 text-q4cl-accent"><i class="fa-solid fa-circle-check" aria-hidden="true"></i></div>
      <h2 class="text-xl font-bold text-q4cl-primary mb-3">{{ $t('お疲れ様でした') }}</h2>
      <p class="text-q4cl-muted text-sm leading-relaxed">{{ $t('ご回答ありがとうございました。このページを閉じてお進みください。') }}</p>
    </div>
  </div>

  <div v-else :class="{ 'already-sent': alreadySent }">
    <!-- ヘッダーカード: タイトル + 概要 -->
    <q4cl-card>
      <h1 id="questionnaire-title" class="text-xl sm:text-2xl font-bold text-q4cl-primary mb-2">{{ $t(title) }}</h1>
      <div v-if="description" class="text-sm text-q4cl-muted prose prose-sm max-w-none" v-html="renderedDescription"></div>
      <!-- 担当部署 / 担当者 / 責任者 / 問い合わせ先 (旧版踏襲: RenderQuestionnaire.vue:56-63, 1043-1048) -->
      <div v-if="contactInfo.hasAny" class="mt-3 text-xs text-q4cl-muted text-right space-y-0.5">
        <p v-if="contactInfo.deptInCharge"><span class="font-semibold">{{ $t('担当部署') }}:</span> {{ $t(contactInfo.deptInCharge) }}</p>
        <p v-if="contactInfo.staffs"><span class="font-semibold">{{ $t('担当者') }}:</span> {{ $t(contactInfo.staffs) }}</p>
        <p v-if="contactInfo.responsiblePerson"><span class="font-semibold">{{ $t('責任者') }}:</span> {{ $t(contactInfo.responsiblePerson) }}</p>
        <p v-if="contactInfo.contact"><span class="font-semibold">{{ $t('問い合わせ先') }}:</span> {{ $t(contactInfo.contact) }}</p>
      </div>
      <div v-if="userProperty && userProperty.name" class="mt-3 pt-3 border-t border-q4cl-border text-sm text-q4cl-muted flex items-center gap-2 flex-wrap">
        <img v-if="userProperty.logo" :src="userProperty.logo" alt="" class="w-7 h-7 rounded" />
        <span>{{ $t('提供') }}: <button type="button" class="link-inline" @click="showAbout">{{ $t(userProperty.name) }}</button></span>
        <button v-if="userProperty.privacyPolicy" type="button" class="ml-auto link-inline" @click="showPrivacy">{{ $t('プライバシーポリシー') }}</button>
      </div>
    </q4cl-card>

    <!-- フォーム本体 -->
    <form @submit.prevent="onSubmit" @input="onFormInput" @change="onFormInput" novalidate aria-labelledby="questionnaire-title">
      <q4cl-card v-for="card in fieldCards" :key="card.key" :title="card.title">
        <!--
          field 間の縦リズムは「親で gap、 子で margin 持たせない」 方式で統一。
          各 field component (FieldFrame / RadioField / CheckboxField / SectionField)
          の root から mb-* を撤去済み。 ここで space-y-5 (= 1.25rem、 旧版 mb-5 と同等)
          を一律 gap として支配 → field の種類 (text vs scale vs radio vs section vs
          separator) が混在しても常に同じ間隔。
          v-show の hidden field は隣接セレクタが OK なので問題なし。
        -->
        <div class="space-y-5">
          <component
            v-for="field in card.fields"
            :key="field.id"
            :is="resolveComponent(field)"
            v-bind="fieldBindings(field)"
            v-show="!field.hidden"
          />
        </div>
      </q4cl-card>

      <!-- 送信状態の SR 通知 (視覚的には隠す) -->
      <div role="status" aria-live="polite" class="sr-only-fallback">{{ liveStatus }}</div>

      <!-- サブスクリプション状態案内 (旧版踏襲: 試用期間警告 / 無償ライセンス案内) -->
      <q4cl-card v-if="subscriptionInfo">
        <p class="text-sm leading-relaxed text-q4cl-ink subscription-info" v-html="subscriptionInfo"></p>
      </q4cl-card>

      <!-- 送信ボタン。 旧版踏襲 (RenderQuestionnaire.vue:1631-1657): 送信後の終了導線は
           持続ボタンではなく完了ダイアログ内の [修正する / 問診票アプリを終了] で提供する。 -->
      <div class="sticky bottom-0 z-20 bg-q4cl-surface/85 backdrop-blur border-t border-q4cl-border p-4 -mx-4 sm:mx-0 sm:rounded-q4cl sm:border sm:mt-2">
        <div class="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
          <button type="submit" class="btn-primary sm:flex-1" :aria-busy="submitting ? 'true' : 'false'">
            <span v-if="submitting" class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" aria-hidden="true"></span>
            {{ submitting ? $t('送信中...') : $t(submitButtonTitle) }}
          </button>
        </div>
      </div>
    </form>

    <!-- モーダル群 -->
    <modal-info v-if="showInfo" :title="infoTitle" :content="infoContent" @close="showInfo = false" />
    <modal-language-selector v-if="showLanguage" @close="showLanguage = false" />
    <modal-sent-history v-if="showHistory" @close="showHistory = false" />
  </div>
</template>

<script>
import Q4clCard from '@/components/ui/Q4clCard.vue'
import painScaleSvg from '@/assets/PainScale.svg'
import TextField from '@/components/fields/TextField.vue'
import TextareaField from '@/components/fields/TextareaField.vue'
import RadioField from '@/components/fields/RadioField.vue'
import CheckboxField from '@/components/fields/CheckboxField.vue'
import DateField from '@/components/fields/DateField.vue'
import TimeField from '@/components/fields/TimeField.vue'
import ScaleField from '@/components/fields/ScaleField.vue'
import PhotoField from '@/components/fields/PhotoField.vue'
import SignpadField from '@/components/fields/SignpadField.vue'
import FigureField from '@/components/fields/FigureField.vue'
import SectionField from '@/components/fields/SectionField.vue'
import EmbedField from '@/components/fields/EmbedField.vue'
import FormattedInputField from '@/components/fields/FormattedInputField.vue'
import EvalField from '@/components/fields/EvalField.vue'
import ModalInfo from '@/components/modals/ModalInfo.vue'
import ModalLanguageSelector from '@/components/modals/ModalLanguageSelector.vue'
import ModalSentHistory from '@/components/modals/ModalSentHistory.vue'
export default {
  name: 'QuestionnaireForm',
  components: {
    'q4cl-card': Q4clCard,
    'text-field': TextField,
    'textarea-field': TextareaField,
    'radio-field': RadioField,
    'checkbox-field': CheckboxField,
    'date-field': DateField,
    'time-field': TimeField,
    'scale-field': ScaleField,
    'photo-field': PhotoField,
    'signpad-field': SignpadField,
    'figure-field': FigureField,
    'section-field': SectionField,
    'embed-field': EmbedField,
    'formatted-input-field': FormattedInputField,
    'eval-field': EvalField,
    'modal-info': ModalInfo,
    'modal-language-selector': ModalLanguageSelector,
    'modal-sent-history': ModalSentHistory
  },
  props: {
    qstDef: { type: Object, required: true },
    userProperty: { type: Object, default: () => ({}) },
    userId: { type: String, required: true },
    qName: { type: String, required: true },
    publicKey: { type: String, default: null },
    answerId: { type: String, required: true },
    mailNotification: { type: Boolean, default: false },
    debug: { type: Boolean, default: false }
  },
  setup(props) {
    const i18n = window.i18nStore;
    // URL クエリの privacyItems 値注入用 (旧版 App.vue:110-128 setParamsFromUrl の踏襲)。
    // ?hash=... の他に ?ptId=... &ptName=... 等が渡ってきたら該当フィールドの初期値にする。
    const route = VueRouter.useRoute();
    const fields = Vue.reactive({});
    const liveStatus = Vue.ref('');         // role=status エリアの読み上げテキスト
    const submitting = Vue.ref(false);
    const alreadySent = Vue.ref(false);
    const terminated = Vue.ref(false);
    // 送信先の answerId。 初回送信で他人の送信済み ID と衝突したら振り直すので props とは別に持つ
    const currentAnswerId = Vue.ref(props.answerId);
    Vue.watch(() => props.answerId, (v) => { currentAnswerId.value = v; });

    // 旧旧版踏襲 (RenderQuestionnaire.vue:610-623): 入力途中の戻る/タブ閉じで全消失を防ぐ。
    // changed は form @input / @change 経由でセット。 送信成功でリセット。
    const changed = Vue.ref(false);
    const showInfo = Vue.ref(false);
    const showLanguage = Vue.ref(false);
    const showHistory = Vue.ref(false);
    const infoTitle = Vue.ref('');
    const infoContent = Vue.ref('');

    const title = Vue.computed(() => props.qstDef.title || props.qName);
    const description = Vue.computed(() => {
      // 旧旧版踏襲 (Q4CL-client_old_version.xml:3422-3433, getJoined):
      // 配列なら "\n" で join (markdown 上は段落結合)。 文字列ならそのまま。
      const d = props.qstDef.description;
      if (!d) return '';
      return Array.isArray(d) ? d.join('\n') : d;
    });
    const renderedDescription = Vue.computed(() => {
      try {
        // 旧版踏襲: i18n を通してから markdown 処理
        const raw = description.value || '';
        const translated = i18n ? i18n.i18n(raw) : raw;
        return DOMPurify.sanitize(marked.parse(translated));
      } catch (e) { return description.value; }
    });
    // 旧旧版踏襲 (RenderQuestionnaire.vue:706-708): alreadySent 時に 送信 → 再送信 に自動置換
    const submitButtonTitle = Vue.computed(() => {
      const base = props.qstDef.submit_button_title || '送信する';
      return alreadySent.value ? base.replace(/送信/g, '再送信') : base;
    });

    // 旧旧版踏襲 (RenderQuestionnaire.vue:1043-1048): 担当部署 / 担当者 / 責任者 / 問い合わせ先
    // staffs と responsible_person は配列なら '; ' で join (旧版同様)
    const contactInfo = Vue.computed(() => {
      const q = props.qstDef || {};
      const joinIfArr = (v) => Array.isArray(v) ? v.join('; ') : v;
      const info = {
        deptInCharge: q.dept_in_charge || '',
        staffs: joinIfArr(q.staffs) || '',
        responsiblePerson: joinIfArr(q.responsible_person) || '',
        contact: q.contact || ''
      };
      info.hasAny = !!(info.deptInCharge || info.staffs || info.responsiblePerson || info.contact);
      return info;
    });

    // 旧旧版踏襲 (RenderQuestionnaire.vue:709-743): サブスクリプション状態の警告/案内
    // - feewaiver === true → 無償ライセンスメッセージ
    // - subscription が feewaiver 以外のキーを持つ → 警告非表示 (有料契約済み)
    // - それ以外 (subscription が空 / feewaiver:false のみ) → 試用期間警告
    // - COVID-19 関連の問診票は無料扱いで抑制
    const subscriptionInfo = Vue.computed(() => {
      try {
        const up = props.userProperty;
        if (!up) return '';
        if ((up.subscription && up.subscription.feewaiver === true) || up.subscriptionStatus === '無料') {
          return 'この問診票システムは Emuyn LLC より ' + (up.name || '') + '様に無償ライセンスされています。';
        }
        // subscriptionStatus は console がログイン時に保存する支払状況。
        // 試用中・期限切れ・未設定は「試用期間中」 を表示 (有効・仮延長中・管理者は非表示)
        const st = up.subscriptionStatus;
        const isTrial = !st || st === '試用中' || st === 'ライセンス期限切れ';
        let info = isTrial ? '<mark>試用期間中</mark>です。送信された回答内容は予告なく削除される場合があります。<a href="https://www.emuyn.net/%E3%81%8A%E5%95%8F%E3%81%84%E5%90%88%E3%82%8F%E3%81%9B%E3%83%95%E3%82%A9%E3%83%BC%E3%83%A0" target="_blank" rel="noopener">お問い合わせはエミュイン合同会社まで</a>お願いします。' : '';
        if (info && /(covid.19|新型コロナ|読み込んでいます)/i.test(title.value || '')) info = '';
        return info;
      } catch (e) { return ''; }
    });

    // ===== フィールド初期化 (旧版 setFields + initializeField + initializeRadioCheckbox) =====
    function downToArray(cake) {
      if (typeof cake === 'object') return Array.isArray(cake) ? cake : Object.keys(cake);
      return String(cake).split('|');
    }

    function initializeRadioCheckbox(field) {
      const esc = window.mixinUtils.escapeFbRtdbKey;
      const fldSel = downToArray(field.selector);
      let objIdx = {};
      if (field.selectoridx || field.selectorindex) {
        const idxArray = downToArray(field.selectoridx || field.selectorindex);
        fldSel.forEach((sel, i) => { objIdx[sel] = idxArray[i]; });
      } else if (typeof field.selector === 'object' && !Array.isArray(field.selector)) {
        objIdx = field.selector;
      } else {
        fldSel.forEach((sel, i) => { objIdx[sel] = i; });
      }
      field.sels = {};
      fldSel.forEach((key) => {
        field.sels[esc(key)] = {
          name: field.name,
          id: field.id + '_' + key,
          value: key,
          checked: field.default ? downToArray(field.default).indexOf(key) >= 0 : false,
          index: objIdx[key] || null
        };
      });
      if (field.type === 'radio' && field.default) field.value = field.default;
    }

    // 旧旧版踏襲 (RenderQuestionnaire.vue:1003-1031): figure/clipart の svgsrc → data URI、
    // 外部 URL → sakura proxy 経由で base64 化して field.src に格納する。
    // (新版は <img> 直接表示で svg:utf8 を扱えるので Canvg 不要)
    //
    // ★ 重要 (Vue 3 reactivity): 引数の field は initializeField 時点ではまだ生オブジェクトで、
    //    `fields[id] = field` の後に reactive proxy 化される。 ここで raw field を直接 mutate すると
    //    template が proxy 経由で読んでいる src の更新を検知できない (旧版が 1029 行で
    //    `this.fields = _.cloneDeep(this.fields)` していたのと同じ落とし穴)。
    //    そこで field.id を保持し、 fetch 完了後に proxy 経由 (fields[id].src = ...) で書き換える。
    function resolveFigureSrcAsync(id) {
      // 同期分は呼び出し元で済ませる前提。 ここでは http 取得のみ非同期化
      const f = fields[id];
      if (!f) return;
      if (!f.src || f.src.indexOf('http') !== 0) return;
      const url = f.src;
      const proxy = 'https://api.emuyn.net/getImgsrcFromImgfile.php?fileurl=' + encodeURIComponent(url);
      fetch(proxy)
        .then((res) => (res.ok ? res.text() : null))
        .then((text) => {
          if (text == null) return;
          const t = text.trim();
          if (!fields[id]) return; // unmount された場合の保護
          if (t.startsWith('data:')) {
            fields[id].src = t;
          } else {
            // svg などが生で返ってきた場合は data URI 化
            fields[id].src = 'data:image/svg+xml;utf8,' + encodeURIComponent(t);
          }
        })
        .catch((e) => console.warn('[QuestionnaireForm] figure proxy fetch failed:', e));
    }

    // 同期処理: svgsrc → data URI 変換のみここで実行 (initializeField の中で完結)
    function resolveFigureSrcSync(field) {
      if (!field.src && field.svgsrc) {
        field.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(field.svgsrc);
      }
    }

    function initializeField(field) {
      switch (field.type) {
        case 'radio':
        case 'checkbox':
          initializeRadioCheckbox(field);
          break;
        case 'date':
          field.format = field.format || 'YYYY/MM/DD';
          break;
        case 'time':
          field.format = field.format || 'HH:mm';
          break;
        case 'getphoto':
          field.photos = field.photos || [];
          break;
        case 'figure':
        case 'clipart':
          // 同期: svgsrc → data URI
          resolveFigureSrcSync(field);
          // 非同期 (http URL → proxy) は setupFields の中で fields[id] 登録後に呼ぶ
          break;
        case 'painscale':
          // 旧版踏襲: VAS face scale。 0〜10 の 11 段階、 SVG 画像で顔を表示。
          field.type = 'scale';
          field.min = 0; field.max = 10;
          field.starttag = 'なし'; field.endtag = '最大';
          field.value = -1;
          field.subtype = 'painscale';
          field.img = field.img || painScaleSvg;
          field.description = field.description || '痛みの程度を数字で表してください';
          break;
        case 'bodytemp':
          field.type = 'scale';
          field.min = field.min || 340; field.max = field.max || 410;
          field.value = field.value || 0;
          field.step = 1;
          field.subtype = 'bodytemp';
          break;
        case 'bloodpressure':
          field.type = 'scale';
          field.min = field.min || 40; field.max = field.max || 240;
          if (!field.value || typeof field.value !== 'object') {
            field.value = { lower: 0, upper: 0 };
          } else {
            field.value = {
              lower: field.value.lower !== undefined ? field.value.lower : 0,
              upper: field.value.upper !== undefined ? field.value.upper : 0
            };
          }
          field.step = field.step || 1;
          field.subtype = 'bloodpressure';
          break;
        case 'text':
        case 'textarea':
          if (typeof field.value === 'undefined') field.value = '';
          if (field.type === 'textarea' && typeof field.text === 'undefined') field.text = '';
          break;
        case 'eval':
        case 'emergency':
        case 'warning':
          if (typeof field.value === 'undefined') field.value = '';
          break;
      }
    }

    function setupFields() {

      // 旧旧版踏襲 (Q4CL-client_old_version.xml:3500-3514):
      // userProperty.privacyItems があるとき、 該当する Q4CL.privacyItems を qstDef.fields
      // の冒頭に prepend する。 privacyItemsRequired にあるものは required=true。
      // disable_privacyitems / disable_privacyitem_requirement で抑制可能。
      const baseFields = [];
      const up = props.userProperty || {};
      if (up.privacyItems && Array.isArray(up.privacyItems) && !props.qstDef.disable_privacyitems) {
        // 旧旧版は reverse() してから unshift で先頭挿入することで、 元の privacyItems 順を維持
        const items = _.cloneDeep(window.Q4CL.privacyItems).reverse();
        const piRequired = up.privacyItemsRequired || [];
        // 旧版踏襲 (App.vue:118-122): URL に ?ptId=12345&ptName=... 等が渡っていれば初期値化。
        // QR コードに患者情報を埋める運用の医療機関で、 患者に再入力させないため。
        const query = (route && route.query) || {};
        items.forEach((item) => {
          if (up.privacyItems.includes(item.name)) {
            if (piRequired.includes(item.name) && !props.qstDef.disable_privacyitem_requirement) {
              item.required = true;
            }
            if (query[item.name]) {
              // radio/checkbox は initializeRadioCheckbox が field.default から sels[k].checked
              // を組み立てるので、 default にも同じ値を入れないと UI に選択が反映されない
              // (例: ?ptSex=男性 → sels['男性'].checked=true & field.value='男性')。
              const v = String(query[item.name]);
              item.value = v;
              item.default = v;
            }
            baseFields.unshift(item);
          }
        });
        if (baseFields.length > 0) baseFields.push({ type: 'separator' });
      }
      const arr = baseFields.concat(props.qstDef.fields || []);

      arr.forEach((rawField, index) => {
        if (!rawField) return;
        const field = _.cloneDeep(rawField);
        field.id = field.id || field.name || `field_${index}`;
        field.hidden = !!field.parentname;
        field.tabindex = index;

        initializeField(field);
        fields[field.id] = field;
        // figure/clipart の外部 URL 解決は reactive 登録後に呼ぶ (proxy 経由で書き換える必要があるため)
        if (field.type === 'figure' || field.type === 'clipart') {
          resolveFigureSrcAsync(field.id);
        }
      });
      window.fieldSerializer.setHiddenProps(fields);
    }

    // ===== セクション単位のカードグルーピング =====
    // section field 自体はカードタイトルに昇格させ、fields からは除外する (二重表示防止)
    const fieldCards = Vue.computed(() => {
      const list = [];
      let current = { key: 'card_0', title: '', fields: [] };
      let idx = 0;
      for (const id in fields) {
        const f = fields[id];
        if (f.type === 'section') {
          if (current.fields.length > 0 || current.title) list.push(current);
          idx++;
          current = { key: 'card_' + idx, title: f.dispname || '', fields: [] };
        } else {
          current.fields.push(f);
        }
      }
      if (current.fields.length > 0 || current.title) list.push(current);
      return list;
    });

    // 入力フィールドの種類 (ラベル + エラー表示が必要なもの)
    const INPUT_TYPES = new Set([
      'text', 'textarea', 'radio', 'checkbox', 'date', 'time', 'scale',
      'getphoto', 'signpad', 'figure', 'formattedInput'
    ]);
    // 装飾フィールドの種類 (ラベル不要、props は field のみ)
    const DECORATIVE_TYPES = new Set([
      'section', 'subsection', 'note', 'clipart', 'embed', 'separator',
      'eval', 'emergency', 'warning'
    ]);

    function resolveComponent(field) {
      const map = {
        text: 'text-field',
        textarea: 'textarea-field',
        radio: 'radio-field',
        checkbox: 'checkbox-field',
        date: 'date-field',
        time: 'time-field',
        scale: 'scale-field',
        getphoto: 'photo-field',
        signpad: 'signpad-field',
        figure: 'figure-field',
        section: 'section-field',
        subsection: 'section-field',
        note: 'section-field',
        clipart: 'section-field',
        separator: 'section-field',
        embed: 'embed-field',
        formattedInput: 'formatted-input-field',
        eval: 'eval-field',
        emergency: 'eval-field',
        warning: 'eval-field'
      };
      return map[field.type] || null;
    }

    // 入力系フィールドには (field, showOptional) を渡し、装飾系には field のみ
    // (旧版は必須エラーをカード全体の background-color: #fef2f2 で示し、
    //  値の有無で動的判定するため、 個別 error prop は不要)
    // showOptional: 必須でないフィールドにのみ 「※任意」 バッジを表示 (デジ庁デザインシステム準拠)
    function fieldBindings(field) {
      if (INPUT_TYPES.has(field.type)) {
        return { field, showOptional: !field.required };
      }
      return { field };
    }

    // 親フィールドの値変化を監視して setHiddenProps を再計算
    // 旧版踏襲: parent の radio/checkbox の sels.checked が変わった時だけ子の hidden を更新する。
    // deep watch で fields 全体をスキャンすると重い + setHiddenProps 自身が field.hidden を
    // 書き換えて watch を再トリガする副作用がある (Vue 3 は値変化なしなら notify しないので
    // 無限ループにはならないが、毎回 deep scan のコストが発生)。
    // → parent 候補 (parentname を持つ field の親) の sels だけを依存に絞った watcher にする。
    const parentFieldNames = Vue.computed(() => {
      const names = new Set();
      for (const id in fields) {
        if (fields[id]?.parentname) names.add(fields[id].parentname);
      }
      return Array.from(names);
    });
    Vue.watch(
      // 各 parent field の sels.checked 状態だけを束ねた配列を依存にする
      () => parentFieldNames.value.map((name) => {
        const f = fields[name];
        if (!f || !f.sels) return '';
        return Object.entries(f.sels).map(([k, v]) => k + ':' + (v.checked ? '1' : '0')).join('|');
      }).join('||'),
      () => {
        window.fieldSerializer.setHiddenProps(fields);
      }
    );

    // ===== 送信 =====
    async function onSubmit() {
      const presentDialog = window.mixinUtils.presentDialog;
      const i18nAs = (s) => (i18n ? i18n.i18nAs(s) : Promise.resolve(s));

      // 再送信確認
      if (alreadySent.value) {
        const s不 = await i18nAs('再送信しない');
        const s再 = await i18nAs('再送信する');
        const r = await presentDialog(
          await i18nAs('再送信しますか?'),
          '',
          await i18nAs('再送信すると、前回送信した内容は新しい内容で上書きされます。'),
          [s不, s再]
        );
        if (r === s不) return;
      }

      submitting.value = true;
      liveStatus.value = await i18nAs('送信処理を確認しています。少々お待ちください。');

      try {
        // 旧旧版踏襲: encKey は onMounted (publickey 取得直後) で setEncryptParams 済み。
        // ここで再生成すると、 figure/signpad/getphoto の onSave で既に
        // encryptStrWithHash した内容と AES key が食い違ってしまう。
        // 既に encryptParams があればそれを使い回す。 無いときだけ (publicKey が
        // 遅れて来た等のレアケース) ここで初期化する。
        let encryptParams = window.encryption.encryptParams;
        if (!encryptParams) {
          if (props.publicKey) {
            encryptParams = await window.encryption.setEncryptParams(props.publicKey);
          } else if (props.debug) {
            console.warn('[debug] publicKey なし — 暗号化をスキップ');
          } else {
            throw new Error('公開鍵が未設定です。問診票を再読込してください。');
          }
        }

        const now = new Date();
        const result = await window.fieldSerializer.process({
          fields,
          i18nAs
        });

        // 必須エラー (旧版踏襲: 個別 field の background は .required-empty クラスが
        // 動的判定で表示するので、 ここでは dialog 表示と focus 移動のみ行う)
        if (result.arrEmptyRequired.length > 0) {
          submitting.value = false;
          const suffix = await i18nAs('を入力してください。');
          liveStatus.value = (await i18nAs('入力が不足している項目があります')) + ` (${result.arrEmptyRequired.length})`;

          const arr = result.arrEmptyRequired.length < 4 ? result.arrEmptyRequired : result.arrEmptyRequired.slice(0, 3);
          const otherWord = await i18nAs('ほか');
          const itemWord = await i18nAs('項目');
          await presentDialog(
            arr.map((fn) => '【' + (fields[fn]?.dispname ? (i18n ? i18n.i18n(fields[fn].dispname) : fields[fn].dispname) : fn) + '】').join(', ') +
              (result.arrEmptyRequired.length < 4 ? '' : ` ${otherWord} ${result.arrEmptyRequired.length - 3} ${itemWord}`) +
              suffix,
            await i18nAs('入力をお願いします'),
            await i18nAs('※ は入力が必須の項目です'),
            ['OK']
          );

          // 最初のエラー項目に focus + scroll
          const firstId = result.arrEmptyRequired[0];
          const elm = document.getElementById(firstId);
          if (elm) {
            if (elm.scrollIntoView) elm.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const focusable = elm.querySelector('input:not([type="hidden"]), textarea, select, [tabindex="0"]');
            if (focusable) {
              setTimeout(() => focusable.focus({ preventScroll: true }), 350);
            }
          }
          return;
        }

        // バリデーションエラー (電話・メール 形式不正)
        if (result.validationErrors.length > 0) {
          submitting.value = false;
          const lines = [];
          for (const e of result.validationErrors) {
            if (e === '電話番号') lines.push(await i18nAs('電話番号は半角数字とハイフンで入力してください (例: 090-1234-5678)'));
            else if (e === 'メールアドレス') lines.push(await i18nAs('メールアドレスは「name@example.com」の形式で入力してください'));
            else lines.push((await i18nAs(e)) + (await i18nAs('が正しくありません')));
          }
          liveStatus.value = (await i18nAs('入力内容に誤りがあります')) + ': ' + result.validationErrors.join(', ');
          await presentDialog(
            await i18nAs('入力内容の修正をお願いします'),
            '',
            lines.join('\n'),
            ['OK']
          );
          return;
        }

        // 3 経路に送信
        const { fields: payload, fieldsTextOnly } = window.fieldSerializer.buildPayloads({
          fields: result.fields,
          evalResults: result.evalResults,
          encryptParams,
          now
        });

        const esc = window.mixinUtils.escapeFbRtdbKey;
        const refPathsFor = (id) => {
          const sub = `${props.userId}/${esc(props.qName)}/${id}`;
          return {
            owner: `questionnaire/answerowners/${sub}`,
            refPath1: `questionnaire/answers/${sub}`,
            refPath2: `questionnaire/answersTextOnly/${sub}`,
            refPath3: `questionnaire/answerlist/${sub}`
          };
        };
        const { refPath1, refPath2, refPath3 } = refPathsFor(currentAnswerId.value);

        const answerlistData = {
          deviceId: window.encryption.md5hex(window.deviceStore.deviceId),
          now: Number(now)
        };

        // デバッグモード (?debug=true) のときだけ。 本番で送信内容を端末のコンソールに残さない
        // debugOpts.payloadLog でゲート (デフォルト on、 トグル/URL 引数で off)
        if (props.debug && window.debugOpts?.payloadLog !== false) {
          console.log('[Q4CL] 送信:', { refPath1, refPath2, refPath3, payload, fieldsTextOnly, answerlistData });
          // debug / playwright での deep object 取り出し用に JSON 化版も出力
          try {
            console.log('[Q4CL:payload]', JSON.stringify({ refPath1, refPath2, refPath3, payload, fieldsTextOnly, answerlistData }));
          } catch (e) { console.warn('[Q4CL:payload] stringify 失敗:', e); }
        }

        // debug モード時は通常 Firebase 送信スキップ。 ただし debugOpts.firebaseWrite=true なら強制実書き込み
        if (props.debug && !window.debugOpts?.firebaseWrite) {
          console.warn('[debug] Firebase 送信スキップ (debug_firebase_write=true で強制送信可)');
        } else {
          const db = firebase.database();
          // 匿名ログインの完了を待つ (最大 5 秒)。 失敗・タイムアウト時は未ログインのまま送信する
          // ページを長時間開いたままで匿名アカウントが定期削除された場合に備え、 送信時点の
          // currentUser を使い、 ログインが切れていればその場で匿名ログインし直す
          const authUser = await Promise.race([
            (window.q4clAuthReady || Promise.resolve(null))
              .then(() => firebase.auth().currentUser || firebase.auth().signInAnonymously().then((c) => c.user))
              .catch(() => null),
            new Promise((resolve) => setTimeout(() => resolve(null), 5000))
          ]);
          const writeAll = async (id) => {
            const p = refPathsFor(id);
            // 送信者記録を先に書く (回答が存在すると記録できないルールのため順序が重要)
            if (authUser) await db.ref(p.owner).set(authUser.uid);
            await db.ref(p.refPath1).set(payload);
            await db.ref(p.refPath2).set(fieldsTextOnly);
            await db.ref(p.refPath3).set(answerlistData);
          };
          const isPermissionDenied = (e) => /PERMISSION_DENIED/i.test(String((e && (e.code || e.message)) || ''));
          for (let attempt = 0; ; attempt++) {
            try {
              await writeAll(currentAnswerId.value);
              break;
            } catch (e) {
              // 初回送信の権限エラー = answerId が他人の送信済み回答と衝突。 ID を振り直して再試行
              if (alreadySent.value || attempt >= 3 || !isPermissionDenied(e)) throw e;
              console.warn('[Q4CL] answerId 衝突のため振り直し:', currentAnswerId.value);
              await new Promise((resolve) => setTimeout(resolve, 150));
              currentAnswerId.value = window.questionnaireStore.generateAnswerId();
            }
          }

          // メール通知
          // 宛先 (施設の loginid) はサーバー側で取得する
          if (props.mailNotification) {
            const mailParams = window.fieldSerializer.buildMailNotificationParams(props.userId, props.qName, currentAnswerId.value);
            axios.post(window.fieldSerializer.NOTIFY_API_URL, mailParams).catch((e) => console.error('mail error:', e));
          }
        }

        // 送信履歴を localStorage に追記
        const labelFieldVal = props.qstDef.label_field && result.clonedFields[props.qstDef.label_field]
          ? result.clonedFields[props.qstDef.label_field].value
          : '';
        window.deviceStore.appendSubmitHistory(
          props.qName,
          currentAnswerId.value,
          labelFieldVal,
          moment(now).format('YYYY/MM/DD HH:mm')
        );

        submitting.value = false;
        alreadySent.value = true;
        // 送信完了したら離脱警告は不要 (旧版踏襲: changed = false)
        changed.value = false;
        const ansLabel = await i18nAs('回答番号');
        liveStatus.value = (await i18nAs('送信が完了しました')) + ' (' + ansLabel + ': ' + currentAnswerId.value + ')';

        // デジタル庁指針準拠: 主要アクション (filled) を右端に 1 つのみ配置。
        // 「問診票アプリを終了」が通常フロー、「修正する」は例外的な副次操作。
        const sFix = await i18nAs('修正する');
        const sExit = await i18nAs('問診票アプリを終了');
        const dlgChoice = await presentDialog(
          await i18nAs('送信完了'),
          await i18nAs('入力内容を暗号化して送信しました'),
          ((() => {
            const m = props.qstDef.message || props.userProperty?.endingMessage;
            return m ? sanitizeMd(i18n ? i18n.i18n(m) : m) : '';
          })()) +
            (props.qstDef.no_answer_id_disp ? '' : `<p>${escapeHtml(ansLabel)}: <strong>${escapeHtml(currentAnswerId.value)}</strong></p>`),
          [sFix, sExit]
        );
        if (dlgChoice === sExit) await exitApp();
      } catch (err) {
        console.error('[Q4CL] 送信エラー:', err);
        submitting.value = false;
        liveStatus.value = await i18nAs('送信に失敗しました。再度お試しください。');
        await presentDialog(
          await i18nAs('送信できません'),
          '',
          escapeHtml(await i18nAs('入力内容の送信に失敗しました。')) + '\n' + escapeHtml(err && err.message ? err.message : ''),
          ['OK']
        );
      }
    }

    function sanitizeMd(s) {
      try { return DOMPurify.sanitize(marked.parse(s)); }
      catch (e) { return s; }
    }

    // presentDialog の sMsg は innerHTML 直挿入なので、 翻訳結果や err.message など
    // 悪意ある HTML が混入し得る文字列を渡す際はエスケープしてから連結する。
    function escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    }

    // 旧旧版踏襲 (RenderQuestionnaire.vue:1784-1808 showUserProperty):
    // userProperty にある全項目を Markdown のリストとして表示する。
    // 個々のフィールドは optional — userProperty が持っているものだけ列挙。
    function showAbout() {
      const up = props.userProperty || {};
      const name = up.name || '';
      const lines = [];
      lines.push('#### ' + name + ' 施設情報');
      if (up.representative) lines.push('* 代表者: ' + up.representative);
      if (up.zipcode) lines.push('* 郵便番号: ' + up.zipcode);
      if (up.address) lines.push('* 住所: ' + up.address);
      if (up.mail) lines.push('* メールアドレス: ' + up.mail);
      if (up.tel1) lines.push('* 電話番号: ' + up.tel1);
      if (up.tel2) lines.push('* 電話番号: ' + up.tel2);
      if (up.fax) lines.push('* FAX番号: ' + up.fax);
      if (up.url) lines.push('* 施設ホームページアドレス: ' + up.url);
      infoTitle.value = name + ' について';
      infoContent.value = lines.join('\n');
      showInfo.value = true;
    }
    function showPrivacy() {
      infoTitle.value = 'プライバシーポリシー';
      infoContent.value = props.userProperty.privacyPolicy || '';
      showInfo.value = true;
    }
    // window.close() は「スクリプトで開いたウィンドウ」 しか閉じれない (Chrome 33+ 制約)。
    // 失敗時はアプリ内で終了画面を表示 (about:blank 遷移は Android 機種依存の黒画面 + 戻るボタン復帰が発生するため廃止)。
    async function exitApp() {
      changed.value = false;
      if (tryCloseWindow()) return;
      window.removeEventListener('popstate', onPopState);
      terminated.value = true;
    }
    function tryCloseWindow() {
      window.close();
      if (window.closed) return true;
      try { window.open('', '_self').close(); } catch (e) { /* ignore */ }
      return window.closed;
    }

    // ヘッダの言語/履歴ボタンとの連携
    function onLangEv() { showLanguage.value = true; }
    function onHistEv() { showHistory.value = true; }

    // DebugPanel からの「fields を寄越せ」 要求に応じる (reactive fields を渡す)
    function onDebugReqFields() {
      window.dispatchEvent(new CustomEvent('q4cl:debug-provide-fields', { detail: { fields } }));
    }
    // DebugPanel の autoFill が走った直後、 changed = true をセット (leave 警告対象に)
    function onDebugMarkedChanged() { changed.value = true; }

    // ===== ブラウザバック防止 + 離脱警告 (旧旧版 RenderQuestionnaire.vue:610-623 踏襲) =====
    function onFormInput() { changed.value = true; }

    // noUiSlider / signpad canvas / ModalDraw が field.value/src を JS で直接書き換えるため
    // form-level @input/@change では検出できない。 fields を deep watch して補う。
    // mount 直後は setupFields / resolveFigureSrcAsync 等の初期化で頻繁に変更されるため、
    // watchArmed でゲートし、初期化が完全に落ち着いた後に arm する。
    const watchArmed = Vue.ref(false);
    Vue.watch(fields, () => {
      if (!watchArmed.value) return;
      changed.value = true;
    }, { deep: true });

    function armFieldWatcher() {
      // 非同期初期化 (figure 画像 fetch 等) の完了を待つため、 nextTick 連打 + setTimeout で安全に遅延
      Vue.nextTick(() => {
        Vue.nextTick(() => {
          setTimeout(() => { watchArmed.value = true; }, 800);
        });
      });
    }
    function onPopState() {
      // 戻るボタン押下時に再度履歴を積み直して同じページに留まる
      // (長押し履歴 UI からは抜けられるが、 単押しの誤タップは防げる — 旧版踏襲)
      try { history.pushState(null, '', location.href); } catch (e) { /* ignore */ }
    }
    function onBeforeUnload(e) {
      if (changed.value) {
        const msg = '入力途中です';
        e.preventDefault();
        e.returnValue = msg;
        return msg;
      }
    }

    Vue.onMounted(async () => {
      setupFields();
      // 旧旧版踏襲 (Q4CL-client_old_version.xml:3295):
      // publickey 取得後に即 setEncryptParams を実行して encKey を準備しておく。
      // これをしないと、 figure/signpad/getphoto の onSave で呼ばれる encryptStrWithHash が
      // encKey 未設定のため fallback "[unencrypted:...]" を返してしまい、 送信データの画像が壊れる。
      if (props.publicKey) {
        try { await window.encryption.setEncryptParams(props.publicKey); }
        catch (e) { console.error('[QuestionnaireForm] setEncryptParams 失敗:', e); }
      }
      // ウェルカムメッセージ表示 (タイトル / 本文は ModalInfo 内で $t/i18n 経由翻訳される)
      // debugOpts.skipWelcome=true なら自動テスト等のためダイアログ抑制
      const rawWm = props.qstDef.welcomemessage || props.userProperty?.welcomeMessage;
      // 旧旧版踏襲: getJoined() — 配列なら \n で join、 文字列ならそのまま
      const wm = Array.isArray(rawWm) ? rawWm.join('\n') : rawWm;
      if (wm && !props.qstDef.disablewelcomemessage && !props.qstDef.disable_welcomemessage
          && !window.debugOpts?.skipWelcome) {
        infoTitle.value = 'ようこそ';
        infoContent.value = wm;
        showInfo.value = true;
      }

      // 旧旧版踏襲 (RenderQuestionnaire.vue:1061-1082): privacyItemsRequired に ptName が含まれず、
      // no_answer_id_disp も false のとき、 回答番号をメモしておく注意を起動時に提示する。
      // (氏名/診察券番号が未入力なら回答番号でしか問診を特定できないため)
      try {
        const pir = props.userProperty?.privacyItemsRequired;
        const needsAnsId = (!pir || !pir.includes('ptName')) && !props.qstDef.no_answer_id_disp;
        if (needsAnsId && !window.debugOpts?.skipAnsIdNotice) {
          const presentDialog = window.mixinUtils.presentDialog;
          const i18nAs = (s) => (i18n ? i18n.i18nAs(s) : Promise.resolve(s));
          // welcome dialog が出ている場合は閉じられるのを待つ
          if (showInfo.value) {
            await new Promise((resolve) => {
              const stop = Vue.watch(showInfo, (v) => { if (!v) { stop(); resolve(); } });
            });
          }
          const ansLabel = await i18nAs('回答番号');
          const body = await i18nAs(
            '氏名や診察券番号を入力いただいていない場合には、 ' +
            '受診の際に回答番号を提示していただく必要がありますので、 ' +
            'お手数ですが忘れないようにメモをお願いします。<br>' +
            'なお、 回答番号は送信時にも表示されますし、 端末内にも保管されます。'
          );
          await presentDialog(
            await i18nAs(title.value),
            '',
            `${ansLabel}: <strong>${currentAnswerId.value}</strong><br><br>${body}`,
            ['OK']
          );
        }
      } catch (e) {
        console.warn('[QuestionnaireForm] needsAnsId dialog failed:', e);
      }

      window.addEventListener('q4cl:open-language-selector', onLangEv);
      window.addEventListener('q4cl:open-sent-history', onHistEv);
      window.addEventListener('q4cl:debug-request-fields', onDebugReqFields);
      window.addEventListener('q4cl:debug-marked-changed', onDebugMarkedChanged);

      // ブラウザバック防止: 履歴を 1 つ積んでおき、 popstate で押し戻す (旧版踏襲)
      try { history.pushState(null, '', location.href); } catch (e) { /* ignore */ }
      window.addEventListener('popstate', onPopState);
      window.addEventListener('beforeunload', onBeforeUnload);

      armFieldWatcher();
    });
    Vue.onBeforeUnmount(() => {
      window.removeEventListener('q4cl:open-language-selector', onLangEv);
      window.removeEventListener('q4cl:open-sent-history', onHistEv);
      window.removeEventListener('q4cl:debug-request-fields', onDebugReqFields);
      window.removeEventListener('q4cl:debug-marked-changed', onDebugMarkedChanged);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('beforeunload', onBeforeUnload);
    });

    return {
      title, description, renderedDescription, submitButtonTitle, subscriptionInfo, contactInfo,
      fieldCards, resolveComponent, fieldBindings,
      submitting, alreadySent, terminated,
      liveStatus,
      onSubmit, exitApp, onFormInput,
      showInfo, infoTitle, infoContent, showLanguage, showHistory,
      showAbout, showPrivacy,
      userProperty: Vue.computed(() => props.userProperty)
    };
  }
};
</script>
